import { useEffect, useRef, useState } from "react";
import { CustomLoader } from "../../../shared/components/custom-loader/custom-loader";
import { FormInput } from "../../../shared/components/form-component/form-input";
import { CUSTOM_VALIDATION } from "../../../shared/validation/validation";
import { HELPER } from "../../../shared/helper/helper";
import { Toast } from "primereact/toast";
import { CustomToast } from "../../../shared/components/alert/custom-toast";
import { Optional } from "../../../shared/components/optional/optional";
import { BACKOFFICE_SERVICE } from "../../../core/services/backoffice-service";
import { SuccessMessage } from "../../../shared/components/success-message/success-message";
import { GLOBAL_MANDATE_DURATION_DAYS_WARNING_MESSAGE } from "../../../shared/constants";

export function MandateConfigurationUpdateForm(props) {
  const toast = useRef(null);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [warningMessage, setWarningMessage] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [alertType, setAlertType] = useState(null);
  const [mandateConfiguration, setMandateConfiguration] = useState({
    maxAllowedDebitAmount: null,
    maxAllowedDebitCount: null,
    maxAllowedDurationInDays: null,
    maxMandatePerMerchantPerAccount: null,
    maxAllowedSingleDebitAmount: null,
  });
  const [formErrors, setFormErrors] = useState({
    maxAllowedDebitAmount: null,
    maxAllowedDebitCount: null,
    maxAllowedDurationInDays: null,
    maxMandatePerMerchantPerAccount: null,
    maxAllowedSingleDebitAmount: null,
  });
  const [isValidForm, setIsValidForm] = useState(false);
  const initialConfig = useRef(null);

  useEffect(() => {
    function validateForm() {
      const hasValidErrors = CUSTOM_VALIDATION.VALID_OBJ_ANY(formErrors);
      const isValidForm = CUSTOM_VALIDATION.IS_VALID_FORM(mandateConfiguration);
      setIsValidForm(isValidForm && hasValidErrors === false);
    }
    validateForm();
  }, [formErrors, mandateConfiguration]);

  useEffect(() => {
    const mapped = {
      ...props.mandateConfiguration,
      maxAllowedDurationInDays:
        props?.mandateConfiguration?.maximumDurationInDays,
      maxAllowedDebitAmount: HELPER.TO_MAJOR_AMOUNT(
        props?.mandateConfiguration?.maxAllowedDebitAmount
      ),
      maxAllowedSingleDebitAmount: HELPER.TO_MAJOR_AMOUNT(
        props?.mandateConfiguration?.maxAllowedSingleDebitAmount
      ),
    };
    setMandateConfiguration(mapped);
    initialConfig.current = mapped;
  }, [props.mandateConfiguration]);

  useEffect(() => {
    const oneYearInDays = 365;
    if (
      mandateConfiguration["maxAllowedDurationInDays"] >= oneYearInDays &&
      warningMessage
    ) {
      setWarningMessage(null);
    }
  }, [mandateConfiguration, warningMessage]);

  const hasChanges =
    initialConfig.current != null &&
    Object.keys(mandateConfiguration).some(
      (key) =>
        String(mandateConfiguration[key] ?? "") !==
        String(initialConfig.current[key] ?? "")
    );

  function submit() {
    setErrorMessage(null);
    const isMaxAllowedDurationInDaysLessThan365 =
      Number(mandateConfiguration["maxAllowedDurationInDays"]) < 365;
    if (isMaxAllowedDurationInDaysLessThan365 && !warningMessage) {
      setAlertType("warning");
      setWarningMessage(GLOBAL_MANDATE_DURATION_DAYS_WARNING_MESSAGE);
      return;
    }
    setLoading(true);
    const globalConfig = {
      maxAllowedDebitAmount: HELPER.TO_MINOR_AMOUNT(
        Number(mandateConfiguration?.maxAllowedDebitAmount)
      ),
      maxAllowedSingleDebitAmount: HELPER.TO_MINOR_AMOUNT(
        Number(mandateConfiguration?.maxAllowedSingleDebitAmount)
      ),
      maxAllowedDebitCount: Number(mandateConfiguration?.maxAllowedDebitCount),
      maxAllowedDurationInDays: Number(
        mandateConfiguration?.maxAllowedDurationInDays
      ),
      maxMandatePerMerchantPerAccount: Number(
        mandateConfiguration?.maxMandatePerMerchantPerAccount
      ),
    };

    BACKOFFICE_SERVICE.INITIATE_MAKER_CHECKER_REQUEST({
      actionType: "GLOBAL_CONFIG_CHANGE",
      globalConfig,
    })
      .then((data) => {
        const requestStatus = data?.result?.requestStatus;
        setSuccessMessage(
          requestStatus === "APPROVED"
            ? "Mandate configuration updated successfully."
            : "Mandate configuration change request submitted for approval."
        );
        setLoading(false);
      })
      .catch((error) => {
        setAlertType("error");
        setErrorMessage(
          error?.status === 403
            ? "You do not have permission to initiate this request"
            : HELPER.PROCESS_ERROR(error)
        );
        setLoading(false);
      });
  }

  function validateForm(e, name, type, field, required) {
    let value = e?.target?.value;
    const isEmpty = CUSTOM_VALIDATION.IS_EMPTY(value);
    const isValidInput = !isEmpty
      ? CUSTOM_VALIDATION.BASIC_VALIDATION(value, type)
      : false;
    if (isValidInput) {
      setMandateConfiguration({
        ...mandateConfiguration,
        [name]: value,
      });
      setFormErrors({ ...formErrors, [name]: null });
    } else {
      let errorMessage = required && isEmpty ? `${field} is required` : null;
      if (!isValidInput) {
        errorMessage = `${field} is invalid`;
      }
      if (isEmpty && !required) {
        setFormErrors({ ...formErrors, [name]: null });
      } else {
        setMandateConfiguration({ ...mandateConfiguration, [name]: value });
        setFormErrors({
          ...formErrors,
          [name]: errorMessage,
        });
      }
    }
  }

  const viewAlert = () => {
    const message = alertType === "error" ? errorMessage : warningMessage;
    if (message) {
      return (
        <div>
          <CustomToast
            title={alertType === "warning" ? "Please Note:" : "Error"}
            description={message}
            type={alertType}
            onClose={
              alertType === "error"
                ? () => {
                    setErrorMessage(null);
                    setAlertType(null);
                  }
                : undefined
            }
          />
        </div>
      );
    }
  };

  function cancelModal() {
    props.closeModal();
  }

  const cancelButton = () => {
    if (!loading) {
      return (
        <button onClick={cancelModal} className="secondary-button">
          Cancel
        </button>
      );
    } else {
      return <div />;
    }
  };

  const submitButton = () => {
    if (!loading) {
      return (
        <button
          onClick={submit}
          className="primary-button"
          disabled={!isValidForm || !hasChanges}
        >
          {warningMessage ? "Continue" : "Submit"}
        </button>
      );
    } else {
      return (
        <div className="pull-up-element-2 p-mt-4">
          <CustomLoader loadingText="Submitting..." />
        </div>
      );
    }
  };

  const renderForm = () => {
    return (
      <div>
        <div className="custom-modal-title p-text-left">
          Update Debit Mandate Configurations
        </div>
        <div className="custom-dialog-subtitle-container p-mb-5"></div>
        <div className="p-grid">
          <div className="p-col-12">
            <div
              className="p-col-12 p-text-left"
              style={{ position: "relative" }}
            >
              <FormInput
                value={mandateConfiguration["maxAllowedDebitAmount"]}
                required={true}
                field="maxAllowedDebitAmount"
                type="AMOUNT"
                error={formErrors["maxAllowedDebitAmount"]}
                fn={validateForm}
                placeholder="Maximum Allowed Debit Amount"
              />
            </div>
            <div
              className="p-col-12 p-text-left"
              style={{ position: "relative" }}
            >
              <FormInput
                value={mandateConfiguration["maxAllowedSingleDebitAmount"]}
                required={true}
                field="maxAllowedSingleDebitAmount"
                type="AMOUNT"
                error={formErrors["maxAllowedSingleDebitAmount"]}
                fn={validateForm}
                placeholder="Maximum Allowed Single Debit Amount"
              />
            </div>
            <div className="p-col-12 p-text-left">
              <FormInput
                value={mandateConfiguration["maxAllowedDebitCount"]}
                required={true}
                field="maxAllowedDebitCount"
                type="NUMBER"
                error={formErrors["maxAllowedDebitCount"]}
                fn={validateForm}
                placeholder="Maximum Allowed Debit Count"
              />
              <p
                className="label"
                style={{
                  fontSize: "0.7rem",
                  opacity: "0.8",
                  marginLeft: "0.5rem",
                  marginTop: "0.5rem",
                }}
              >
                Note: 0 (Zero) equals infinity
              </p>
            </div>
            <div className="p-col-12 p-text-left">
              <FormInput
                value={mandateConfiguration["maxAllowedDurationInDays"]}
                required={true}
                field="maxAllowedDurationInDays"
                type="NUMBER"
                error={formErrors["maxAllowedDurationInDays"]}
                fn={validateForm}
                placeholder="Maximum Allowed Duration In Days"
              />
            </div>
            <div className="p-col-12 p-text-left">
              <FormInput
                value={mandateConfiguration["maxMandatePerMerchantPerAccount"]}
                required={true}
                field="maxMandatePerMerchantPerAccount"
                type="NUMBER"
                error={formErrors["maxMandatePerMerchantPerAccount"]}
                fn={validateForm}
                placeholder="Maximum Allowed Mandate Per Merchant Per Account"
              />
            </div>
            <div className="p-col-12">{viewAlert()}</div>
            <div className="p-col-12">
              <div className="">
                <div className="p-grid">
                  <div className={loading ? "p-col-12" : "p-col-6"}>
                    {cancelButton()}
                  </div>
                  <div className={loading ? "p-col-12" : "p-col-6"}>
                    {submitButton()}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div>
      <div>
        <Toast ref={toast} />
      </div>
      <Optional show={!successMessage}>
        <div className="p-pb-2">{renderForm()}</div>
      </Optional>
      <Optional show={successMessage}>
        <SuccessMessage
          message={successMessage}
          closeModal={() => props.closeModal(true)}
        />
      </Optional>
    </div>
  );
}
